/**
 * Work Items API
 *
 * API functions for work item operations including AI enrichment.
 */

import { api, ApiError, type AiServed } from "./client";
import { withTimeout } from "./breakdown";
import type {
  WorkItem,
  WorkItemId,
  EnrichWorkItemRequest,
  EnrichmentResponse,
  WorkItemTeaserResponse,
  ChildBreakdownProposal,
} from "./types";

export interface CsvColumnMapping {
  title: number;
  description?: number;
  item_type?: number;
  status?: number;
  priority?: number;
}

export interface ImportRowError {
  row: number;
  message: string;
}

export interface ImportResult {
  created: number;
  skipped: number;
  errors: ImportRowError[];
}

/**
 * Trigger AI enrichment for a work item
 *
 * Analyzes the work item and returns:
 * - Related work items in the project
 * - Suggested labels based on content
 * - Suggested priority level
 * - Complexity assessment
 * - Actionable suggestions
 *
 * Results are cached for 1 hour. Use force: true to regenerate.
 */
export async function importCsv(
  projectId: string,
  file: File,
  mapping: CsvColumnMapping,
): Promise<ImportResult> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("mapping", JSON.stringify(mapping));

  const response = await fetch(`/api/projects/${projectId}/import/csv`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    if (
      response.status === 401 &&
      !window.location.pathname.startsWith("/login") &&
      !window.location.pathname.startsWith("/pending")
    ) {
      window.location.href = "/login";
      throw new ApiError(401, "Unauthorized");
    }
    const data = await response.json().catch(() => null);
    throw new ApiError(
      response.status,
      (data as { error?: string })?.error ?? response.statusText,
      data,
    );
  }

  return response.json() as Promise<ImportResult>;
}

export interface SuggestedCsvMapping {
  title?: number;
  description?: number;
  item_type?: number;
  status?: number;
  priority?: number;
}

export async function suggestCsvMapping(
  projectId: string,
  headers: string[],
  sampleRows: string[][],
): Promise<SuggestedCsvMapping> {
  return api.post<SuggestedCsvMapping>(
    `/projects/${projectId}/import/csv/suggest-mapping`,
    { headers, sample_rows: sampleRows },
  );
}

export interface GitHubLabel {
  name: string;
  color?: string;
}

export interface GitHubIssue {
  number: number;
  title: string;
  body: string | null;
  html_url: string;
  state: string;
  labels: GitHubLabel[];
  created_at: string;
  updated_at: string;
}

export interface GitHubIssueImportItem {
  number: number;
  title: string;
  body: string | null;
  labels: string[];
  state: string;
}

export async function fetchGithubIssues(
  repoLinkId: string,
  state: string = "open",
  page: number = 1,
  perPage: number = 30,
): Promise<GitHubIssue[]> {
  return api.get<GitHubIssue[]>(
    `/github/repos/${repoLinkId}/issues?state=${state}&page=${page}&per_page=${perPage}`,
  );
}

export async function importGithubIssues(
  projectId: string,
  repoLinkId: string,
  issues: GitHubIssueImportItem[],
): Promise<ImportResult> {
  return api.post<ImportResult>(`/projects/${projectId}/import/github`, {
    repo_link_id: repoLinkId,
    issues,
  });
}

export async function enrichWorkItem(
  workItemId: WorkItemId,
  options?: EnrichWorkItemRequest,
): Promise<AiServed<EnrichmentResponse>> {
  return api.postAi<EnrichmentResponse>(
    `/work-items/${workItemId}/enrich`,
    options ?? {},
  );
}

/** `force` asks for the standard model even while the quota fallback is on (FLOW-794). */
export async function fetchAITeaser(
  workItemId: WorkItemId,
  force = false,
): Promise<AiServed<WorkItemTeaserResponse>> {
  return api.postAi<WorkItemTeaserResponse>(
    `/work-items/${workItemId}/ai-teaser`,
    { force },
  );
}

/**
 * Ask the AI whether a work item is complex enough to warrant breaking down
 * into children, and propose a checklist of them if so (FLOW-747). An AI
 * round trip, so it shares the breakdown client's long timeout.
 */
export async function proposeChildren(
  workItemId: WorkItemId,
  force: boolean,
): Promise<ChildBreakdownProposal> {
  return withTimeout((signal) =>
    api.post<ChildBreakdownProposal>(
      `/work-items/${workItemId}/children/propose`,
      { force },
      { signal },
    ),
  );
}

/**
 * Fetch a single work item
 *
 * A module-level function so callers can be tested by mocking this module —
 * `useWorkItem` inlines the same request for its own resource.
 */
export async function getWorkItem(id: WorkItemId): Promise<WorkItem> {
  return api.get<WorkItem>(`/work-items/${id}`);
}
